import { readStorage, writeStorage } from './safeStorage';
import { StorageKey } from './storageKeys';
import { parseStoredTime } from './exportReminder';

/**
 * The three device-local timestamps behind the export reminder, as an external store so the
 * settings page and the top bar hint update together (and across tabs, via the `storage` event).
 */
export interface ReminderTimes {
  readonly lastExportAt: number | null;
  readonly firstUseAt: number | null;
  readonly dismissedAt: number | null;
}

const listeners = new Set<() => void>();

export function readReminderTimes(): ReminderTimes {
  return {
    lastExportAt: parseStoredTime(readStorage(StorageKey.LastExportAt)),
    firstUseAt: parseStoredTime(readStorage(StorageKey.FirstUseAt)),
    dismissedAt: parseStoredTime(readStorage(StorageKey.ExportReminderDismissedAt)),
  };
}

/** A primitive that changes whenever any of the three values changes (what `useSyncExternalStore` compares). */
export function reminderSnapshot(): string {
  const times = readReminderTimes();
  return `${times.lastExportAt ?? ''}|${times.firstUseAt ?? ''}|${times.dismissedAt ?? ''}`;
}

export function subscribeReminder(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

/** Call after writing one of the keys in this tab. */
export function emitReminderChange(): void {
  for (const listener of [...listeners]) listener();
}

export function recordFirstUse(nowMs: number): void {
  if (readStorage(StorageKey.FirstUseAt) !== null) return;
  if (writeStorage(StorageKey.FirstUseAt, String(nowMs))) emitReminderChange();
}

export function snoozeExportReminder(nowMs: number): void {
  if (writeStorage(StorageKey.ExportReminderDismissedAt, String(nowMs))) emitReminderChange();
}
